/**
 * Event system for real-time feature flag updates.
 *
 * This module provides custom events that are dispatched when feature flags
 * are updated, allowing components to react to changes in real-time.
 */

import type { FeatureFlagUpdateEventDetail } from '#~/posthog/types.js';

/**
 * Event names for feature flag system.
 */
export const FEATURE_FLAG_EVENTS = {
  /** Dispatched when feature flags are updated */
  FLAGS_UPDATED: 'posthog:flags:updated',

  /** Dispatched when feature flag cache is invalidated */
  CACHE_INVALIDATED: 'posthog:cache:invalidated',

  /** Dispatched when auto-refresh state changes */
  AUTO_REFRESH_STATE_CHANGED: 'posthog:auto-refresh:state-changed',

  /** Dispatched when a specific flag changes */
  FLAG_CHANGED: 'posthog:flag:changed'
} as const;

/**
 * Custom event for feature flag updates.
 */
export interface FeatureFlagUpdateEvent extends CustomEvent<FeatureFlagUpdateEventDetail> {
  readonly type: typeof FEATURE_FLAG_EVENTS.FLAGS_UPDATED;
}

/**
 * Custom event for cache invalidation.
 */
export interface FeatureFlagCacheInvalidatedEvent extends CustomEvent<{ timestamp: number }> {
  readonly type: typeof FEATURE_FLAG_EVENTS.CACHE_INVALIDATED;
}

/**
 * Custom event for auto-refresh state changes.
 */
export interface AutoRefreshStateChangedEvent extends CustomEvent<{
  enabled: boolean;
  interval: number;
  timestamp: number;
}> {
  readonly type: typeof FEATURE_FLAG_EVENTS.AUTO_REFRESH_STATE_CHANGED;
}

/**
 * Custom event for individual flag changes.
 */
export interface FlagChangedEvent extends CustomEvent<{
  flag: string;
  value: boolean;
  previousValue: boolean | undefined;
  timestamp: number;
}> {
  readonly type: typeof FEATURE_FLAG_EVENTS.FLAG_CHANGED;
}

/**
 * Union type of all feature flag events.
 */
export type FeatureFlagEvent =
  | FeatureFlagUpdateEvent
  | FeatureFlagCacheInvalidatedEvent
  | AutoRefreshStateChangedEvent
  | FlagChangedEvent;

/**
 * Utility class for dispatching feature flag events.
 */
export class FeatureFlagEventDispatcher {
  /**
   * Dispatch a feature flags updated event.
   */
  static dispatchFlagsUpdated(detail: FeatureFlagUpdateEventDetail): void {
    const event = new CustomEvent(FEATURE_FLAG_EVENTS.FLAGS_UPDATED, {
      detail,
      bubbles: false,
      cancelable: false
    }) as FeatureFlagUpdateEvent;

    window.dispatchEvent(event);
  }

  /**
   * Dispatch a cache invalidated event.
   */
  static dispatchCacheInvalidated(): void {
    const event = new CustomEvent(FEATURE_FLAG_EVENTS.CACHE_INVALIDATED, {
      detail: { timestamp: Date.now() },
      bubbles: false,
      cancelable: false
    }) as FeatureFlagCacheInvalidatedEvent;

    window.dispatchEvent(event);
  }

  /**
   * Dispatch an auto-refresh state changed event.
   */
  static dispatchAutoRefreshStateChanged(enabled: boolean, interval: number): void {
    const event = new CustomEvent(FEATURE_FLAG_EVENTS.AUTO_REFRESH_STATE_CHANGED, {
      detail: { enabled, interval, timestamp: Date.now() },
      bubbles: false,
      cancelable: false
    }) as AutoRefreshStateChangedEvent;

    window.dispatchEvent(event);
  }

  /**
   * Dispatch a flag changed event.
   */
  static dispatchFlagChanged(
    flag: string,
    value: boolean,
    previousValue: boolean | undefined
  ): void {
    const event = new CustomEvent(FEATURE_FLAG_EVENTS.FLAG_CHANGED, {
      detail: { flag, value, previousValue, timestamp: Date.now() },
      bubbles: false,
      cancelable: false
    }) as FlagChangedEvent;

    window.dispatchEvent(event);
  }
}

/**
 * Type-safe event listener helpers.
 */
export class FeatureFlagEventListeners {
  /**
   * Add a listener for feature flags updated events.
   */
  static onFlagsUpdated(callback: (event: FeatureFlagUpdateEvent) => void): () => void {
    const listener = callback as EventListener;
    window.addEventListener(FEATURE_FLAG_EVENTS.FLAGS_UPDATED, listener);

    return () => {
      window.removeEventListener(FEATURE_FLAG_EVENTS.FLAGS_UPDATED, listener);
    };
  }

  /**
   * Add a listener for cache invalidated events.
   */
  static onCacheInvalidated(
    callback: (event: FeatureFlagCacheInvalidatedEvent) => void
  ): () => void {
    const listener = callback as EventListener;
    window.addEventListener(FEATURE_FLAG_EVENTS.CACHE_INVALIDATED, listener);

    return () => {
      window.removeEventListener(FEATURE_FLAG_EVENTS.CACHE_INVALIDATED, listener);
    };
  }

  /**
   * Add a listener for auto-refresh state changes.
   */
  static onAutoRefreshStateChanged(
    callback: (event: AutoRefreshStateChangedEvent) => void
  ): () => void {
    const listener = callback as EventListener;
    window.addEventListener(FEATURE_FLAG_EVENTS.AUTO_REFRESH_STATE_CHANGED, listener);

    return () => {
      window.removeEventListener(FEATURE_FLAG_EVENTS.AUTO_REFRESH_STATE_CHANGED, listener);
    };
  }

  /**
   * Add a listener for individual flag changes.
   */
  static onFlagChanged(callback: (event: FlagChangedEvent) => void): () => void {
    const listener = callback as EventListener;
    window.addEventListener(FEATURE_FLAG_EVENTS.FLAG_CHANGED, listener);

    return () => {
      window.removeEventListener(FEATURE_FLAG_EVENTS.FLAG_CHANGED, listener);
    };
  }
}
